import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import pageT from "../i18n/pageTranslations";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleFromPath,
  localeLocationPath,
  localizePath,
  stripLocalePrefix,
} from "../utils/localePaths";

const siteT = {
  ko: {
    // Nav groups
    fortune: "운세",
    tarotAndGunghap: "타로·궁합",
    services: "서비스",
    community: "커뮤니티",
    tools: "도구",
    myMyeongri: "나의 명리",
    // Nav items
    aiFortune: "AI 사주 분석",
    celebritySaju: "유명인 사주",
    liveLounge: "라이브 라운지",
    astrologyChart: "서양 점성술",
    tarotReading: "타로 리딩",
    gunghapAnalysis: "궁합 분석",
    chokjumGallery: "촉점 갤러리",
    sajuBoard: "사주팔자 게시판",
    encyclopedia: "학습자료",
    psychtest: "심리테스트",
    sajuFarm: "사주농원",
    sajuMap: "점집지도",
    myeongriPortal: "명리보감",
    siteFranchise: "사이트 분양",
    mypage: "마이페이지",
    chargePoints: "포인트 충전",
    developerPortal: "개발자 포털",
    // User menu
    notifications: "알림",
    usageHistory: "이용 기록",
    adminERP: "관리자 ERP",
    // Header
    home: "홈",
    login: "로그인",
    register: "가입",
    logout: "로그아웃",
    lightMode: "라이트 모드",
    darkMode: "다크 모드",
    user: "사용자",
    // Common
    loading: "로딩 중...",
    language: "언어",
    langName: "한국어",
    submit: "전송",
    cancel: "취소",
    confirm: "확인",
    save: "저장",
    delete: "삭제",
    edit: "수정",
    close: "닫기",
    back: "뒤로",
    next: "다음",
    previous: "이전",
    noData: "데이터가 없습니다",
    copy: "복사",
    copied: "복사됨",
    search: "검색",
    // Auth pages
    email: "이메일",
    password: "비밀번호",
    passwordConfirm: "비밀번호 확인",
    name: "이름",
    nickname: "닉네임",
    forgotPassword: "비밀번호를 잊으셨나요?",
    loginLoading: "로그인 중...",
    loginFailed: "로그인에 실패했습니다",
    orDivider: "또는",
    googleLogin: "Google로 로그인",
    kakaoLogin: "카카오톡으로 로그인",
    tossLogin: "토스로 로그인",
    noAccount: "아직 계정이 없으신가요?",
    hasAccount: "이미 계정이 있으신가요?",
    signUp: "회원가입",
    signUpLoading: "가입 중...",
    welcomeMsg: "사주팔자AI에 오신 것을 환영합니다",
    emailPlaceholder: "이메일 주소",
    passwordPlaceholder: "비밀번호",
    tossWebOnly: "토스 로그인은 토스 앱 내에서만 사용할 수 있습니다.",
    tossLoginError: "토스 로그인 중 오류가 발생했습니다.",
    tossLoginFailed: "토스 로그인에 실패했습니다.",
    // Strength
    strengthVWeak: "매우 약함",
    strengthWeak: "약함",
    strengthMedium: "보통",
    strengthStrong: "강함",
    strengthVStrong: "매우 강함",
    // Password reset
    findPassword: "비밀번호 찾기",
    sendResetLink: "재설정 링크 보내기",
    backToLogin: "로그인으로 돌아가기",
    resetPassword: "비밀번호 재설정",
    newPassword: "새 비밀번호",
    changePassword: "비밀번호 변경",
    processing: "처리 중...",
    // Register
    namePlaceholder: "실명",
    nicknamePlaceholder: "화면에 표시될 이름",
    passwordMin: "8자 이상",
    passwordReenter: "비밀번호 재입력",
    passwordMismatch: "비밀번호가 일치하지 않습니다",
    passwordMinError: "비밀번호는 8자 이상이어야 합니다",
    signUpSuccess: "회원가입이 완료되었습니다. 로그인해주세요.",
    signUpFailed: "회원가입에 실패했습니다",
    agreeTermsPrefix: "가입 시",
    termsOfService: "이용약관",
    privacyPolicy: "개인정보처리방침",
    agreeTermsSuffix: "에 동의합니다",
  },
  en: {
    fortune: "Fortune",
    tarotAndGunghap: "Tarot & Compatibility",
    services: "Services",
    community: "Community",
    tools: "Tools",
    myMyeongri: "My Destiny",
    aiFortune: "AI Saju Analysis",
    celebritySaju: "Celebrity Saju",
    liveLounge: "Live Lounge",
    astrologyChart: "Astrology",
    tarotReading: "Tarot Reading",
    gunghapAnalysis: "Compatibility",
    chokjumGallery: "Gallery",
    sajuBoard: "Saju Forum",
    encyclopedia: "Learning Resources",
    psychtest: "Psych Test",
    sajuFarm: "Saju Farm",
    sajuMap: "Fortune Map",
    myeongriPortal: "Destiny Portal",
    siteFranchise: "Site Franchise",
    mypage: "My Page",
    chargePoints: "Charge Points",
    developerPortal: "Developer Portal",
    notifications: "Notifications",
    usageHistory: "Usage History",
    adminERP: "Admin ERP",
    home: "Home",
    login: "Login",
    register: "Sign Up",
    logout: "Logout",
    lightMode: "Light Mode",
    darkMode: "Dark Mode",
    user: "User",
    loading: "Loading...",
    language: "Language",
    langName: "English",
    submit: "Submit",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    noData: "No data",
    copy: "Copy",
    copied: "Copied",
    search: "Search",
    email: "Email",
    password: "Password",
    passwordConfirm: "Confirm Password",
    name: "Name",
    nickname: "Nickname",
    forgotPassword: "Forgot password?",
    loginLoading: "Logging in...",
    loginFailed: "Login failed",
    orDivider: "or",
    googleLogin: "Login with Google",
    kakaoLogin: "Login with Kakao",
    tossLogin: "Login with Toss",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    signUp: "Sign Up",
    signUpLoading: "Signing up...",
    welcomeMsg: "Welcome to Saju AI",
    emailPlaceholder: "Email address",
    passwordPlaceholder: "Password",
    tossWebOnly: "Toss login is only available within the Toss app.",
    tossLoginError: "An error occurred during Toss login.",
    tossLoginFailed: "Toss login failed.",
    strengthVWeak: "Very Weak",
    strengthWeak: "Weak",
    strengthMedium: "Medium",
    strengthStrong: "Strong",
    strengthVStrong: "Very Strong",
    findPassword: "Find Password",
    sendResetLink: "Send Reset Link",
    backToLogin: "Back to Login",
    resetPassword: "Reset Password",
    newPassword: "New Password",
    changePassword: "Change Password",
    processing: "Processing...",
    namePlaceholder: "Full name",
    nicknamePlaceholder: "Display name",
    passwordMin: "8+ characters",
    passwordReenter: "Re-enter password",
    passwordMismatch: "Passwords do not match",
    passwordMinError: "Password must be at least 8 characters",
    signUpSuccess: "Registration complete. Please log in.",
    signUpFailed: "Registration failed",
    agreeTermsPrefix: "By signing up, you agree to the",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    agreeTermsSuffix: "",
  },
  ja: {
    fortune: "占い",
    tarotAndGunghap: "タロット・相性",
    services: "サービス",
    community: "コミュニティ",
    tools: "ツール",
    myMyeongri: "私の命理",
    aiFortune: "AI 四柱分析",
    celebritySaju: "有名人の四柱",
    liveLounge: "ライブラウンジ",
    astrologyChart: "西洋占星術",
    tarotReading: "タロットリーディング",
    gunghapAnalysis: "相性分析",
    chokjumGallery: "ギャラリー",
    sajuBoard: "四柱掲示板",
    encyclopedia: "学習資料",
    psychtest: "心理テスト",
    sajuFarm: "四柱農園",
    sajuMap: "占い店マップ",
    myeongriPortal: "命理宝鑑",
    siteFranchise: "サイト分譲",
    mypage: "マイページ",
    chargePoints: "ポイントチャージ",
    developerPortal: "開発者ポータル",
    notifications: "通知",
    usageHistory: "利用履歴",
    adminERP: "管理者 ERP",
    home: "ホーム",
    login: "ログイン",
    register: "新規登録",
    logout: "ログアウト",
    lightMode: "ライトモード",
    darkMode: "ダークモード",
    user: "ユーザー",
    loading: "読み込み中...",
    language: "言語",
    langName: "日本語",
    submit: "送信",
    cancel: "キャンセル",
    confirm: "確認",
    save: "保存",
    delete: "削除",
    edit: "編集",
    close: "閉じる",
    back: "戻る",
    next: "次へ",
    previous: "前へ",
    noData: "データなし",
    copy: "コピー",
    copied: "コピー済",
    search: "検索",
    email: "メール",
    password: "パスワード",
    passwordConfirm: "パスワード確認",
    name: "名前",
    nickname: "ニックネーム",
    forgotPassword: "パスワードをお忘れですか？",
    loginLoading: "ログイン中...",
    loginFailed: "ログインに失敗しました",
    orDivider: "または",
    googleLogin: "Googleでログイン",
    kakaoLogin: "カカオでログイン",
    tossLogin: "Tossでログイン",
    noAccount: "アカウントをお持ちでないですか？",
    hasAccount: "すでにアカウントをお持ちですか？",
    signUp: "新規登録",
    signUpLoading: "登録中...",
    welcomeMsg: "四柱推命AIへようこそ",
    emailPlaceholder: "メールアドレス",
    passwordPlaceholder: "パスワード",
    tossWebOnly: "TossログインはTossアプリ内でのみ利用できます。",
    tossLoginError: "Tossログイン中にエラーが発生しました。",
    tossLoginFailed: "Tossログインに失敗しました。",
    strengthVWeak: "非常に弱い",
    strengthWeak: "弱い",
    strengthMedium: "普通",
    strengthStrong: "強い",
    strengthVStrong: "非常に強い",
    findPassword: "パスワードを探す",
    sendResetLink: "リセットリンクを送信",
    backToLogin: "ログインに戻る",
    resetPassword: "パスワードリセット",
    newPassword: "新しいパスワード",
    changePassword: "パスワード変更",
    processing: "処理中...",
    namePlaceholder: "本名",
    nicknamePlaceholder: "表示名",
    passwordMin: "8文字以上",
    passwordReenter: "パスワード再入力",
    passwordMismatch: "パスワードが一致しません",
    passwordMinError: "パスワードは8文字以上必要です",
    signUpSuccess: "登録完了。ログインしてください。",
    signUpFailed: "登録に失敗しました",
    agreeTermsPrefix: "登録時",
    termsOfService: "利用規約",
    privacyPolicy: "プライバシーポリシー",
    agreeTermsSuffix: "に同意します",
  },
  ru: {
    fortune: "Гороскоп",
    tarotAndGunghap: "Таро и совместимость",
    services: "Сервисы",
    community: "Сообщество",
    tools: "Инструменты",
    myMyeongri: "Моя судьба",
    aiFortune: "AI Анализ Сажу",
    celebritySaju: "Сажу знаменитостей",
    liveLounge: "Лайв-лаундж",
    astrologyChart: "Астрология",
    tarotReading: "Таро расклад",
    gunghapAnalysis: "Совместимость",
    chokjumGallery: "Галерея",
    sajuBoard: "Форум Сажу",
    encyclopedia: "Обучение",
    psychtest: "Психотест",
    sajuFarm: "Сажу Ферма",
    sajuMap: "Карта гаданий",
    myeongriPortal: "Портал судьбы",
    siteFranchise: "Франшиза сайта",
    mypage: "Мой профиль",
    chargePoints: "Пополнить баланс",
    developerPortal: "Dev Портал",
    notifications: "Уведомления",
    usageHistory: "История",
    adminERP: "Админ ERP",
    home: "Главная",
    login: "Войти",
    register: "Регистрация",
    logout: "Выйти",
    lightMode: "Светлая тема",
    darkMode: "Тёмная тема",
    user: "Пользователь",
    loading: "Загрузка...",
    language: "Язык",
    langName: "Русский",
    submit: "Отправить",
    cancel: "Отмена",
    confirm: "Подтвердить",
    save: "Сохранить",
    delete: "Удалить",
    edit: "Редактировать",
    close: "Закрыть",
    back: "Назад",
    next: "Далее",
    previous: "Назад",
    noData: "Нет данных",
    copy: "Скопировать",
    copied: "Скопировано",
    search: "Поиск",
    email: "Эл. почта",
    password: "Пароль",
    passwordConfirm: "Подтвердите пароль",
    name: "Имя",
    nickname: "Никнейм",
    forgotPassword: "Забыли пароль?",
    loginLoading: "Вход...",
    loginFailed: "Ошибка входа",
    orDivider: "или",
    googleLogin: "Войти через Google",
    kakaoLogin: "Войти через Kakao",
    tossLogin: "Войти через Toss",
    noAccount: "Ещё нет аккаунта?",
    hasAccount: "Уже есть аккаунт?",
    signUp: "Регистрация",
    signUpLoading: "Регистрация...",
    welcomeMsg: "Добро пожаловать в Saju AI",
    emailPlaceholder: "Адрес эл. почты",
    passwordPlaceholder: "Пароль",
    tossWebOnly: "Вход через Toss доступен только в приложении Toss.",
    tossLoginError: "Ошибка входа через Toss.",
    tossLoginFailed: "Не удалось войти через Toss.",
    strengthVWeak: "Очень слабый",
    strengthWeak: "Слабый",
    strengthMedium: "Средний",
    strengthStrong: "Сильный",
    strengthVStrong: "Очень сильный",
    findPassword: "Восстановить пароль",
    sendResetLink: "Отправить ссылку",
    backToLogin: "Вернуться к входу",
    resetPassword: "Сброс пароля",
    newPassword: "Новый пароль",
    changePassword: "Изменить пароль",
    processing: "Обработка...",
    namePlaceholder: "Полное имя",
    nicknamePlaceholder: "Отображаемое имя",
    passwordMin: "Не менее 8 символов",
    passwordReenter: "Повторите пароль",
    passwordMismatch: "Пароли не совпадают",
    passwordMinError: "Пароль должен быть не менее 8 символов",
    signUpSuccess: "Регистрация завершена. Войдите в аккаунт.",
    signUpFailed: "Ошибка регистрации",
    agreeTermsPrefix: "Регистрируясь, вы соглашаетесь с",
    termsOfService: "Условиями использования",
    privacyPolicy: "Политикой конфиденциальности",
    agreeTermsSuffix: "",
  },
  de: {
    fortune: "Horoskop",
    tarotAndGunghap: "Tarot & Kompatibilität",
    services: "Dienste",
    community: "Community",
    tools: "Tools",
    myMyeongri: "Mein Schicksal",
    aiFortune: "AI Saju-Analyse",
    celebritySaju: "Promi-Saju",
    liveLounge: "Live-Lounge",
    astrologyChart: "Astrologie",
    tarotReading: "Tarot-Lesung",
    gunghapAnalysis: "Kompatibilität",
    chokjumGallery: "Galerie",
    sajuBoard: "Saju-Forum",
    encyclopedia: "Lernmaterial",
    psychtest: "Psychotest",
    sajuFarm: "Saju-Garten",
    sajuMap: "Wahrsagerkarte",
    myeongriPortal: "Schicksalsportal",
    siteFranchise: "Website-Lizenz",
    mypage: "Meine Seite",
    chargePoints: "Punkte aufladen",
    developerPortal: "Entwicklerportal",
    notifications: "Benachrichtigungen",
    usageHistory: "Nutzungsverlauf",
    adminERP: "Admin ERP",
    home: "Startseite",
    login: "Anmelden",
    register: "Registrieren",
    logout: "Abmelden",
    lightMode: "Heller Modus",
    darkMode: "Dunkler Modus",
    user: "Benutzer",
    loading: "Laden...",
    language: "Sprache",
    langName: "Deutsch",
    submit: "Senden",
    cancel: "Abbrechen",
    confirm: "Bestätigen",
    save: "Speichern",
    delete: "Löschen",
    edit: "Bearbeiten",
    close: "Schließen",
    back: "Zurück",
    next: "Weiter",
    previous: "Zurück",
    noData: "Keine Daten",
    copy: "Kopieren",
    copied: "Kopiert",
    search: "Suche",
    email: "E-Mail",
    password: "Passwort",
    passwordConfirm: "Passwort bestätigen",
    name: "Name",
    nickname: "Spitzname",
    forgotPassword: "Passwort vergessen?",
    loginLoading: "Anmeldung...",
    loginFailed: "Anmeldung fehlgeschlagen",
    orDivider: "oder",
    googleLogin: "Mit Google anmelden",
    kakaoLogin: "Mit Kakao anmelden",
    tossLogin: "Mit Toss anmelden",
    noAccount: "Noch kein Konto?",
    hasAccount: "Bereits ein Konto?",
    signUp: "Registrieren",
    signUpLoading: "Registrierung...",
    welcomeMsg: "Willkommen bei Saju AI",
    emailPlaceholder: "E-Mail-Adresse",
    passwordPlaceholder: "Passwort",
    tossWebOnly: "Toss-Login ist nur in der Toss-App verfügbar.",
    tossLoginError: "Fehler beim Toss-Login.",
    tossLoginFailed: "Toss-Login fehlgeschlagen.",
    strengthVWeak: "Sehr schwach",
    strengthWeak: "Schwach",
    strengthMedium: "Mittel",
    strengthStrong: "Stark",
    strengthVStrong: "Sehr stark",
    findPassword: "Passwort finden",
    sendResetLink: "Reset-Link senden",
    backToLogin: "Zurück zur Anmeldung",
    resetPassword: "Passwort zurücksetzen",
    newPassword: "Neues Passwort",
    changePassword: "Passwort ändern",
    processing: "Verarbeitung...",
    namePlaceholder: "Vollständiger Name",
    nicknamePlaceholder: "Anzeigename",
    passwordMin: "Mind. 8 Zeichen",
    passwordReenter: "Passwort erneut eingeben",
    passwordMismatch: "Passwörter stimmen nicht überein",
    passwordMinError: "Passwort muss mindestens 8 Zeichen lang sein",
    signUpSuccess: "Registrierung abgeschlossen. Bitte anmelden.",
    signUpFailed: "Registrierung fehlgeschlagen",
    agreeTermsPrefix: "Mit der Registrierung stimmen Sie den",
    termsOfService: "Nutzungsbedingungen",
    privacyPolicy: "Datenschutzrichtlinie",
    agreeTermsSuffix: "zu",
  },
};

export const LANG_OPTIONS = [
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const legacyLang = new URLSearchParams(location.search).get("lang");
  const validLegacyLang = SUPPORTED_LOCALES.includes(legacyLang) ? legacyLang : null;
  const pathLang = getLocaleFromPath(location.pathname);
  const savedLang = localStorage.getItem("site_lang");
  const validSavedLang = SUPPORTED_LOCALES.includes(savedLang) ? savedLang : null;
  const shouldRestoreSavedLang = !validLegacyLang && pathLang === DEFAULT_LOCALE && validSavedLang && validSavedLang !== DEFAULT_LOCALE;
  const lang = validLegacyLang || (shouldRestoreSavedLang ? validSavedLang : pathLang);

  useEffect(() => {
    if (!validLegacyLang) return;

    const legacyParams = new URLSearchParams(location.search);
    legacyParams.delete("lang");
    const search = legacyParams.toString();
    const logicalPath = stripLocalePrefix(location.pathname);
    const target = localizePath(
      `${logicalPath}${search ? `?${search}` : ""}${location.hash}`,
      validLegacyLang,
    );
    localStorage.setItem("site_lang", validLegacyLang);
    navigate(target, { replace: true, state: location.state });
  }, [location.hash, location.pathname, location.search, location.state, navigate, validLegacyLang]);

  useEffect(() => {
    if (!shouldRestoreSavedLang) return;
    navigate(localeLocationPath(location, validSavedLang), { replace: true, state: location.state });
  }, [location, location.state, navigate, shouldRestoreSavedLang, validSavedLang]);

  const setLang = useCallback((code) => {
    if (!SUPPORTED_LOCALES.includes(code)) return;

    const params = new URLSearchParams(location.search);
    params.delete("lang");
    const target = localeLocationPath({
      ...location,
      search: params.toString() ? `?${params.toString()}` : "",
    }, code);
    localStorage.setItem("site_lang", code);
    navigate(target, { state: location.state });
  }, [location, navigate]);

  useEffect(() => {
    document.documentElement.lang = lang;
    if (!shouldRestoreSavedLang) localStorage.setItem("site_lang", lang);
  }, [lang, shouldRestoreSavedLang]);

  const t = useMemo(() => ({
    ...(siteT[lang] || siteT.ko),
    ...(pageT[lang] || pageT.ko),
  }), [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, LANG_OPTIONS, defaultLang: DEFAULT_LOCALE }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
